import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImagePlus, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/hero`;

export function HeroPhoto() {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(Date.now());
  const [hasCustom, setHasCustom] = useState(true);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/hero", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("hero_saved"));
      setHasCustom(true);
      setVersion(Date.now());
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const reset = async () => {
    try {
      await api.delete("/hero");
      toast.success(t("hero_reset"));
      setHasCustom(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="hero-photo-panel" className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-[#E8DFD5] rounded-2xl p-4">
      <div className="w-20 h-14 rounded-lg overflow-hidden bg-[#F6F1E9] border border-[#E8DFD5] shrink-0">
        {hasCustom && (
          <img src={`${HERO_URL}?v=${version}`} alt="" data-testid="hero-preview" onError={() => setHasCustom(false)} className="w-full h-full object-cover" />
        )}
      </div>
      <Label className="text-xs font-semibold text-[#4A2C2A] flex-1 min-w-[120px]">{t("hero_photo")}</Label>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="hero-file-input" />
      <Button data-testid="change-hero-btn" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} className="rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] gap-1.5">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {t("change_photo")}
      </Button>
      {hasCustom && (
        <Button data-testid="reset-hero-btn" size="sm" variant="outline" onClick={reset} className="rounded-full border-[#E8DFD5] text-[#4A2C2A] gap-1.5">
          <RotateCcw className="w-4 h-4" /> {t("reset_photo")}
        </Button>
      )}
    </div>
  );
}
